import { Injectable } from '@angular/core';
import {
    CanActivate,
    Router,
    RouterStateSnapshot,
    ActivatedRouteSnapshot,
    UrlTree,
} from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { TokenStorageService } from 'viewbid-shared-lib';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

/**
 * Blocks restricted account pages (profile pages, watchlist, invoice
 * actions) for anyone without a real, current session — and does so
 * synchronously-as-far-as-the-router-can-tell, via an Observable the router
 * waits on, so the guarded page never renders first.
 *
 * Reads Auth0's own isAuthenticated$ rather than reconstructing the Auth0
 * SPA-JS SDK's localStorage cache key and checking for its bare presence: a
 * raw presence check doesn't know about token expiry, so a stale/invalidated
 * session (e.g. revoked or expired since the entry was written) would still
 * read as "authenticated". isAuthenticated$ reflects the SDK's own
 * expiry-aware session state.
 *
 * isAuthenticated$ emitting false only means the cached session flag
 * (sessionCheckExpiryDays, default 1 day) has lapsed, not that the refresh
 * token is gone — so a false is retried with getAccessTokenSilently(), the
 * same renewal the rest of the app relies on, before treating the user as
 * signed out.
 */
@Injectable({
    providedIn: 'root',
})
export class AuthGuard implements CanActivate {
    constructor(
        private router: Router,
        private tokenStorageService: TokenStorageService,
        private authService: AuthService,
    ) {}

    canActivate(
        _route: ActivatedRouteSnapshot,
        state: RouterStateSnapshot,
    ): Observable<boolean | UrlTree> {
        // If masquerading, consider it as authenticated
        if (this.tokenStorageService.isMasquerading()) {
            return of(true);
        }

        return this.authService.isAuthenticated$.pipe(
            take(1),
            switchMap((isAuthenticated: boolean) =>
                isAuthenticated
                    ? of(true)
                    : this.authService.getAccessTokenSilently().pipe(
                          map(() => true),
                          catchError(() => of(false)),
                      ),
            ),
            map((isAuthenticated: boolean) =>
                isAuthenticated
                    ? true
                    : // Carry the page the user was trying to reach so login
                      // returns them there instead of /home. A UrlTree keeps
                      // the cancel-and-redirect one atomic router decision —
                      // there's no errorPath configured, so on a session-check
                      // error auth0-angular fires its own redirect too, and
                      // an imperative navigate() here would race it.
                      this.router.createUrlTree(['/login'], {
                          queryParams: { returnUrl: state.url },
                      }),
            ),
        );
    }
}
